import { useEffect, useRef, useState } from 'react';

const SETTLE_DELAY = 250;
const SAFETY_DELAY = 600;

interface UseReportSectionNavigationParams {
  tableSections: { key: string }[];
  sections: Record<string, string>;
  sectionsAIF001: Record<string, string>;
  isAIF001: boolean;
  isStaticTemplate: boolean;
}

export function useReportSectionNavigation({
  tableSections,
  sections,
  sectionsAIF001,
  isAIF001,
  isStaticTemplate
}: UseReportSectionNavigationParams) {
  const virtuosoRef = useRef<any>(null);
  const sectionRefs = useRef<Record<string, any>>({});
  const visibleSectionKeys = isAIF001
    ? Object.keys(sectionsAIF001 || {})
    : Object.keys(sections || {});
  const firstVisibleSectionKey = visibleSectionKeys[0];
  const visibleSectionKeySignature = visibleSectionKeys.join('|');

  // While a click-driven scroll is in flight, `navTargetRef` holds the clicked
  // section so Virtuoso's scroll tracking can't reassign the highlight to a
  // neighbour; `navSettleTimerRef` releases the lock once scrolling has settled.
  const navTargetRef = useRef<string | null>(null);
  const navSettleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(navSettleTimerRef.current), []);

  const [activeSection, setActiveSection] = useState<string>(firstVisibleSectionKey);

  useEffect(() => {
    if (!firstVisibleSectionKey) return;
    setActiveSection(prev => (visibleSectionKeys.includes(prev) ? prev : firstVisibleSectionKey));
  }, [firstVisibleSectionKey, visibleSectionKeySignature]);

  const onNavigateToSection = sectionId => {
    if (!isAIF001) {
      const index = tableSections.findIndex(item => item.key === sectionId);
      if (index < 0 || !virtuosoRef.current) return;
      // Lock the highlight to the clicked section, then jump to it.
      navTargetRef.current = sectionId;
      setActiveSection(sectionId);
      virtuosoRef.current.scrollToIndex({ index, align: 'start' });
      // Safety release in case Virtuoso reports no scroll (e.g. already in view).
      clearTimeout(navSettleTimerRef.current);
      navSettleTimerRef.current = setTimeout(() => {
        navTargetRef.current = null;
      }, SAFETY_DELAY);
      return;
    }

    const element = document.getElementById(sectionId);
    const container = document.getElementById(isStaticTemplate ? 'content-2' : 'content');
    if (element) {
      const sectionPosition = element.offsetTop - (container?.offsetTop ?? 0) - 20;
      container?.scrollTo({ top: sectionPosition, behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Scroll-driven active tracking (Virtuoso rangeChanged). Ignored while a
  // click-driven jump is locked, so re-measure events can't steal the highlight.
  const onChangeActiveSection = value => {
    if (navTargetRef.current) return;
    setActiveSection(value);
  };

  // Virtuoso isScrolling. Keep the lock while the jump (and its correction pass)
  // is moving; once it has been still briefly, re-assert the clicked section and
  // release the lock so manual scrolling resumes normal tracking.
  const onReportScrollingChange = isScrolling => {
    if (!navTargetRef.current) return;
    clearTimeout(navSettleTimerRef.current);
    if (isScrolling) return;
    setActiveSection(navTargetRef.current);
    navSettleTimerRef.current = setTimeout(() => {
      navTargetRef.current = null;
    }, SETTLE_DELAY);
  };

  return {
    virtuosoRef,
    sectionRefs,
    activeSection,
    onNavigateToSection,
    onChangeActiveSection,
    onReportScrollingChange
  };
}
